import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatRp, formatDateID, statusLabel, statusColor, todayISO } from "@/lib/format";
import Header from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Send, Edit, Trash2, AlertCircle } from "lucide-react";

export default function PjDashboard() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [reports, setReports] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [classId, setClassId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([api.get("/pj/classes"), api.get("/pj/reports")]);
      setClasses(c.data);
      setReports(r.data);
      if (c.data.length === 1) setClassId(c.data[0].id);
    } catch (e) { toast.error("Gagal memuat data"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (user && user.role !== "pj") return <Navigate to="/infaq" replace />;
  if (user && user.must_change_password) return <Navigate to="/infaq/ganti-sandi" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!classId) return toast.error("Pilih kelas");
    const amt = parseInt(amount);
    if (!amt || amt <= 0) return toast.error("Nominal harus lebih dari 0");
    setLoading(true);
    try {
      const { data } = await api.post("/pj/reports", { date, class_id: classId, amount: amt, note });
      if (data.duplicate_warning) {
        toast.warning("Peringatan: ada laporan serupa (kelas, tanggal, nominal sama).");
      } else {
        toast.success("Draf laporan disimpan");
      }
      setAmount(""); setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setLoading(false); }
  };

  const sendReport = async (id) => {
    try {
      await api.post(`/pj/reports/${id}/submit`);
      toast.success("Laporan diajukan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengajukan"); }
  };

  const delReport = async (id) => {
    if (!window.confirm("Hapus draf ini?")) return;
    try {
      await api.delete(`/pj/reports/${id}`);
      toast.success("Draf dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/pj/reports/${editing.id}`, {
        date: editing.date, amount: parseInt(editing.amount), note: editing.note,
      });
      toast.success("Perubahan disimpan");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const classMap = Object.fromEntries(classes.map((c) => [c.id, c.name]));

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header siteName="Dashboard PJ Kelas" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1B4D3E] mb-6">Catat Infak Kelas</h1>

        {classes.length === 0 ? (
          <Card className="p-6 border-amber-200 bg-amber-50 flex items-start gap-3" data-testid="pj-no-class-alert">
            <AlertCircle className="text-amber-600 mt-1" />
            <div className="text-sm text-amber-900">
              Anda belum ditugaskan ke kelas apa pun. Hubungi bendahara untuk pengaturan.
            </div>
          </Card>
        ) : (
          <Card className="p-5 sm:p-6 mb-6 border-[#E2E8F0]" data-testid="pj-form-card">
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Tanggal</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} max={todayISO()} required data-testid="pj-date-input" />
              </div>
              <div>
                <Label>Kelas</Label>
                <Select value={classId} onValueChange={setClassId}>
                  <SelectTrigger data-testid="pj-class-select"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Total uang diterima (Rp)</Label>
                <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder="Contoh: 50000" data-testid="pj-amount-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>Catatan internal (opsional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan singkat untuk pengurus" data-testid="pj-note-input" />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={loading} className="w-full sm:w-auto bg-[#1B4D3E] hover:bg-[#143E32]" data-testid="pj-save-draft-btn">
                  <Plus size={16} className="mr-2" /> {loading ? "Menyimpan..." : "Simpan Draf"}
                </Button>
              </div>
            </form>
            <p className="text-xs text-[#94A3B8] mt-4">
              Catat seluruh uang yang diterima pada hari ini. Jika sebuah keluarga menitipkan infak untuk seminggu,
              catat seluruh uang pada hari uang diterima. Jangan membaginya ke tanggal mendatang.
            </p>
          </Card>
        )}

        <h2 className="text-xl font-bold text-[#1B4D3E] mb-3">Riwayat Laporan</h2>
        <Card className="overflow-x-auto border-[#E2E8F0]" data-testid="pj-reports-card">
          <table className="w-full text-sm">
            <thead className="bg-[#EAF2EF] text-[#1B4D3E]">
              <tr>
                <th className="py-3 px-3 text-left">Tanggal</th>
                <th className="py-3 px-3 text-left">Kelas</th>
                <th className="py-3 px-3 text-right">Nominal</th>
                <th className="py-3 px-3 text-left">Status</th>
                <th className="py-3 px-3 text-left">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-[#94A3B8]">Belum ada laporan.</td></tr>
              )}
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-[#F1F5F9]" data-testid={`pj-report-row-${r.id}`}>
                  <td className="py-3 px-3 whitespace-nowrap">{formatDateID(r.date)}</td>
                  <td className="py-3 px-3">{classMap[r.class_id] || "-"}</td>
                  <td className="py-3 px-3 text-right font-semibold">{formatRp(r.amount)}</td>
                  <td className="py-3 px-3">
                    <Badge className={statusColor[r.status]}>{statusLabel[r.status]}</Badge>
                    {r.returned_reason && (
                      <div className="text-xs text-red-700 mt-1">Alasan: {r.returned_reason}</div>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex gap-2 flex-wrap">
                      {(r.status === "draft" || r.status === "dikembalikan") && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => { setEditing({ ...r }); setEditOpen(true); }} data-testid={`pj-edit-btn-${r.id}`}><Edit size={12} /></Button>
                          <Button size="sm" className="bg-[#1B4D3E] hover:bg-[#143E32]" onClick={() => sendReport(r.id)} data-testid={`pj-submit-btn-${r.id}`}><Send size={12} className="mr-1" /> Ajukan</Button>
                        </>
                      )}
                      {r.status === "draft" && (
                        <Button size="sm" variant="outline" onClick={() => delReport(r.id)} data-testid={`pj-delete-btn-${r.id}`}><Trash2 size={12} /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </main>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Laporan</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Tanggal</Label>
                <Input type="date" value={editing.date} max={todayISO()} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="pj-edit-date" />
              </div>
              <div>
                <Label>Nominal</Label>
                <Input type="number" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="pj-edit-amount" />
              </div>
              <div>
                <Label>Catatan</Label>
                <Textarea value={editing.note || ""} onChange={(e) => setEditing({ ...editing, note: e.target.value })} data-testid="pj-edit-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Batal</Button>
            <Button onClick={saveEdit} className="bg-[#1B4D3E] hover:bg-[#143E32]" data-testid="pj-edit-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
